import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { getLanguage } from '@works-web/settings/modules/browser';
import { DialogProvider } from '@works-web/settings/modules/dialog';
import { Env } from '@works-web/settings/modules/env';
import { initI18n } from '@works-web/settings/modules/i18n';
import { PageProvider, MenuItemId } from '@works-web/settings/modules/page';
import { injectIntlAndTimezonePolyfill } from '@works-web/settings/modules/polyfill';
import { ToastProvider } from '@works-web/settings/modules/toast';
import NotFoundScene from '@works-web/settings/scenes/not-found';
import UnknownScene from '@works-web/settings/scenes/unknown';
import * as fallbackTranslations from '@works-web/settings/translations';
import axios from 'axios';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { AppErrorBoundary } from '~/settings/__modules__/component';
import { IE } from '~/settings/__modules__/utils/BrowserService';
import { getTimeZones } from '~/settings/__modules__/utils/moment';
import { sendNeloLog } from '~/settings/__modules__/utils/sendlog';
import { AppData, CommonService } from '~/settings/service/CommonService';
import { ModalProvider } from './__modules__/modal/';
import AutoReplySub from './__scenes__/autoreply/sub';
import BlockUsersSub from './__scenes__/blockUsers/sub';
import MainScene from './__scenes__/main';
import * as translations from './__translations__';
import './settings.css';

const language = getLanguage();

window.process = {};
window.process.TARGET = Env.TARGET;
window.process.PHASE = Env.PHASE;

// IE favicon.icon显示问题  GDCWEBMSG-5060
if (IE) {
  let link = document.querySelector("link[rel*='icon']");
  document.getElementsByTagName('head')[0].removeChild(link);
  let linkDom = document.createElement('link');
  linkDom.type = 'image/x-icon';
  linkDom.rel = 'shortcut icon';
  linkDom.href = `${
    Env.Env.ServiceURL.NOTIFY
  }/settings/favicon.ico?${new Date().getTime()}`;
  document.getElementsByTagName('head')[0].appendChild(linkDom);
}

window.onerror = function (msg, url, lineNo, columnNo, error) {
  const errInfo = {
    body: {
      errormsg: msg,
      errorurl: url,
      lineNo,
      columnNo,
      'Error object': JSON.stringify(error.stack || {}),
    },
    logLevel: 'error',
  };
  sendNeloLog(errInfo);
};

axios
  .get('/p/contact/v2/mobile/init')
  .then(({ data }) => {
    const { userId, domainId } = data;
    AppData.CurrentUser = data;
    return Promise.all([
      axios.get(
        `/p/admin/api/authority/members/${userId}?domainId=${domainId}`,
      ),
      axios.get(`/p/admin/api/theme/domains/${domainId}`),
      axios.get(`/p/admin/domains/${domainId}`),
    ]);
  })
  .then(([memberResp, themeResp, domainInfoResp]) => {
    const memberAuthResponse = memberResp?.data?.data;
    const themeResponse = themeResp?.data?.data;
    const domainStatusCode = domainInfoResp?.data?.domainStatusCode;
    const domainJoinYmdt = domainInfoResp.data.joinYmdt;
    const availableServices = memberAuthResponse.onServiceCodes;
    const hasCampaignCodeBanner =
      memberAuthResponse.onFunctionCodes.includes('F044');

    switch (domainStatusCode) {
      case '100': // 도메인 등록대기, 약관 미동의
        return window.location.replace(
          `${Env.ServiceURL.AUTH}/view/noauth/waitDomain/alertAndGoAdmin`,
        );
      case '200': // 도메인 정상
        break;
      case '300': // 도메인 일시정지
        return window.location.replace(
          `${Env.ServiceURL.AUTH}/view/noauth/stopDomain/alertAndGoAdmin`,
        );
    }

    const menuDisplay = {
      [MenuItemId.PERSONAL_INFO]: true,
      [MenuItemId.SECURITY]: true,
      [MenuItemId.NOTIFY]: true,
      [MenuItemId.BOARD]: availableServices.includes('home_web'),
      [MenuItemId.MESSAGE]: availableServices.includes('message_web'),
      [MenuItemId.MAIL]: availableServices.includes('mail_web'),
      [MenuItemId.CALENDAR]: availableServices.includes('calendar_web'),
      [MenuItemId.CONTACT]: availableServices.includes('contact_web'),
      [MenuItemId.DRIVE]: availableServices.includes('drive_web'),
      [MenuItemId.CLOVANOTE]: availableServices.includes('cnote_web'),
      [MenuItemId.CXTALK]: availableServices.includes('cstalk_web'),
    };

    const authorized = availableServices.includes('message_web');
    if (!authorized) {
      window.location.replace(
        `${
          Env.ServiceURL.AUTH
        }/view/noauth/noServiceAuth?requestUrl=${encodeURIComponent(
          window.location.href,
        )}`,
      );
      return;
    }
    createRoot(document.getElementById('root')).render(
      <BrowserRouter>
        <PageProvider
          requires={[
            initI18n({
              language,
              translations,
              fallbackTranslations,
              debug: Env.PHASE === 'alpha',
            }),
            injectIntlAndTimezonePolyfill(),
            CommonService.init(),
            getTimeZones(),
          ]}
          language={language}
          menuDisplay={menuDisplay}
          gnbBackgroundColor={themeResponse.webMainColor}
          gnbFontColor={themeResponse.webFontColor}
          hasCampaignCodeBanner={hasCampaignCodeBanner}
        >
          <AppErrorBoundary logError={true}>
            <ToastProvider>
              <DialogProvider>
                <ModalProvider>
                  <Switch>
                    <Route path="/settings" exact>
                      <MainScene domainJoinYmdt={domainJoinYmdt} />
                    </Route>
                    <Route path="/settings/autoreply" exact>
                      <AutoReplySub isExternal={true} />
                    </Route>
                    <Route path="/settings/autoreplyinternal" exact>
                      <AutoReplySub isExternal={false} />
                    </Route>
                    <Route path="/settings/blockusers" exact>
                      <BlockUsersSub />
                    </Route>
                    <Route path="*">
                      <NotFoundScene />
                    </Route>
                  </Switch>
                </ModalProvider>
              </DialogProvider>
            </ToastProvider>
          </AppErrorBoundary>
        </PageProvider>
      </BrowserRouter>,
    );
  })
  .catch((err) => {
    console.error(err);
    createRoot(document.getElementById('root')).render(
      <BrowserRouter>
        <PageProvider
          requires={[
            initI18n({
              language,
              translations,
              fallbackTranslations,
              debug: Env.PHASE === 'alpha',
            }),
            injectIntlAndTimezonePolyfill(),
          ]}
          language={language}
        >
          <UnknownScene />
        </PageProvider>
      </BrowserRouter>,
    );
  });
